import React, { useState, useEffect } from "react";
import { Card, Flex, Text, Button, Badge } from "@radix-ui/themes";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AuthMetaDialog } from "./AuthMetaDialog";
import { useActivateMeta, useGetMetaStatus, useGetAllBrands } from "hooks";
import { useLocationContext } from "context";
import { PermissionAlertDialog } from "components";

export function BrowseMetaCard({ userProfile }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [entityConfigs, setEntityConfigs] = useState([]); // called entity because it encompasses both non-default brands config data and independant location config data
  const [isLoading, setIsLoading] = useState(false);
  const [progressValue, setProgressValue] = useState(0);
  const [showPermissionAlert, setShowPermissionAlert] = useState(false);

  const activateMeta = useActivateMeta();
  const [openDialog, setOpenDialog] = useState(false);

  const { data: metaStatusData, isLoading: isStatusLoading } = useGetMetaStatus(userProfile.organisation_id);

  const { locationsData } = useLocationContext();
  const { data: brandsData, isLoading: isLoadingBrands, error: brandsError } = useGetAllBrands(userProfile.organisation_id);

  // State to hold filtered data
  const [brands, setBrands] = useState([]);
  const [independentLocations, setIndependentLocations] = useState([]);
  
  useEffect(() => {
    if (brandsData && locationsData) {
      // Find the default brand
      const defaultBrandItem = brandsData.find((brand) => brand.is_default);

      // Filter out the default brand and get non-default brands
      const nonDefaultBrands = brandsData.filter((brand) => !brand.is_default);
      setBrands(nonDefaultBrands);

      // Filter locations that use the default brand
      const defaultLocations = locationsData.filter((location) => location.brand_id === defaultBrandItem?.id || location.brand_id === null);
      setIndependentLocations(defaultLocations);
    }
  }, [brandsData, locationsData]);

  const isMetaActive = () => {
    if (isStatusLoading) return false;
    return metaStatusData?.data?.is_active === true;
  };

  const handleOpenChange = (isOpen) => {
    setOpenDialog(isOpen);
  };

  const handleConnectClick = () => {
    // Check if user is owner
    const isOwner = userProfile.user_role === "Owner";
    
    if (isOwner) {
      handleOpenChange(true);
    } else {
      setShowPermissionAlert(true);
    }
  };
  
  const handleSave = async () => {
    // Validate all configs
    let isValid = true;

    setEntityConfigs(prev => prev.map(config => {
      const hasPixel = (config.pixelId || "").trim() !== "";
      const hasToken = (config.accessToken || "").trim() !== "";
      const newConfig = { ...config }
      // Apply red border flags exactly like ActiveMetaIntegration
      if (hasToken && !hasPixel) {
        newConfig.isPixelIdValid = false;
        newConfig.isAccessTokenValid = true; // token itself is fine
        isValid = false;
      } else if (!hasToken && hasPixel) {
        newConfig.isAccessTokenValid = false;
        newConfig.isPixelIdValid = true; // pixel itself is fine
        isValid = false;
      } else {
        newConfig.isAccessTokenValid = true;
        newConfig.isPixelIdValid = true;
      }
      return newConfig;
    }));

    if (!isValid) return;

    setIsLoading(true);
    setProgressValue(10);

    try {
      const result = await activateMeta.mutateAsync({
        organisation_id: userProfile.organisation_id,
        configs: entityConfigs.map(({ id, type, accessToken, pixelId }) => ({
          id,
          type,
          access_token: accessToken,
          pixel_id: pixelId,
        })),
      });

      setProgressValue(50);
      if (result?.data?.status === "success") {
        queryClient.invalidateQueries(["meta-status"]);
        navigate("/activation-success", {
          state: {
            description: "Your customer data will now sync with your meta accounts.",
            integrationType: "meta", // Add this to identify we're coming from Airship
          },
        });
      } else {
        throw new Error(result?.data?.message || "Failed to connect meta accounts");
      }
    } catch (error) {
      // console.error("Validation error:", error);
      // Should we console.error() the errors here in prod
    } finally {
      setIsLoading(false);
      setProgressValue(0);
    }
  };

  return (
    <>
      <Card
        style={{
          display: "flex",
          width: "100%",
          padding: "var(--space-4)",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Flex align="center" gap="4">
          <Flex
            style={{
              borderRadius: "var(--space-1)",
              background: "#FFFFFF",
              border: "1px solid var(--gray-a6)",
            }}
            height="40px"
            width="40px"
            p="2"
            justify="center"
            align="center"
          >
            <img src={require("../../../assets/images/meta.png")} alt="meta" style={{ width: "24px" }} />
          </Flex>
          <Flex gap="3" align="start">
            <Flex direction="column" justify="center" align="start">
              <Text size="4" weight="medium">Meta</Text>
              <Text size="1" color="gray">Advertising platform</Text>
            </Flex>
            <Badge size="3" color="amber">Paid feature</Badge>
          </Flex>
        </Flex>

        <Flex align="center">
          {isMetaActive() ? (
            <Badge size="1" variant="soft" color="green">
              Connected
            </Badge>
          ) : (
            <Button onClick={handleConnectClick} size="2" variant="soft" color="gray">
              Connect
            </Button>
          )}
        </Flex>
      </Card>

      <AuthMetaDialog
        locationsData={independentLocations}
        allLocationsData={locationsData}
        brandsData={brands}
        isOpen={openDialog}
        onOpenChange={handleOpenChange}
        onSave={handleSave}
        entityConfigs={entityConfigs}
        setEntityConfigs={setEntityConfigs}
        isLoading={isLoading}
        progressValue={progressValue}
      />

      <PermissionAlertDialog
        isOpen={showPermissionAlert}
        onOpenChange={setShowPermissionAlert}
        message="Only owners can perform this action"
      />
    </>
  );
}
